import { useEffect, useRef, useState } from "react";
import { supabase, setRememberMe } from "@/lib/supabase";
import { T, F, Over } from "@/lib/bricks";
import {
  RESEND_COOLDOWN_SECONDS,
  OTP_LENGTH,
  cleanOtp,
  formatPhoneDisplay,
  isPhoneProviderDisabled,
  phoneAuthErrorMessage,
  retryAfterSeconds,
  sendPhoneOtp,
  verifyPhoneOtp,
} from "@/lib/phoneAuth";

const inputStyle = (p) => ({
  marginTop: 8, width: "100%", padding: "14px 16px",
  border: `1px solid ${p.hairS}`, borderRadius: 2,
  fontSize: 15, fontFamily: F.display, color: p.ink,
  background: "transparent", outline: "none", boxSizing: "border-box",
});

const linkBtn = { background: "none", border: "none", cursor: "pointer", color: T.brickDeep, fontSize: 12, padding: 0, fontFamily: F.display };

/**
 * "Continue with phone": signInWithOtp({ phone }) then verifyOtp({ phone, token, type: 'sms' }).
 * onSession(session) runs after a successful verify; onBack returns to email/Google.
 * onUnavailable fires when Supabase reports the phone provider is disabled.
 */
export default function PhoneAuthPanel({ p, mode = "login", remember = true, onSession, onBack, onUnavailable }) {
  const [phoneInput, setPhoneInput] = useState("");
  const [phone, setPhone] = useState(""); // E.164 once a code was sent
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    timer.current = setTimeout(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearTimeout(timer.current);
  }, [cooldown]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (busy || cooldown > 0) return;
    setError(""); setInfo("");
    setBusy(true);
    try {
      const target = phone || phoneInput;
      const { phone: e164, error: err } = await sendPhoneOtp(supabase, target, { createUser: true });
      if (err) {
        setError(phoneAuthErrorMessage(err, "send"));
        if (isPhoneProviderDisabled(err)) onUnavailable?.();
        const wait = retryAfterSeconds(err);
        if (wait) setCooldown(wait);
        return;
      }
      setPhone(e164);
      setCode("");
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setInfo(`We texted a ${OTP_LENGTH}-digit code to ${formatPhoneDisplay(e164)}.`);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setError("");
    setBusy(true);
    try {
      setRememberMe(remember);
      const { session, error: err } = await verifyPhoneOtp(supabase, phone, code);
      if (err) {
        setError(phoneAuthErrorMessage(err, "verify"));
        return;
      }
      await onSession?.(session);
    } catch (err) {
      setError(phoneAuthErrorMessage(err, "verify"));
    } finally {
      setBusy(false);
    }
  };

  const changeNumber = () => {
    setPhone(""); setCode(""); setError(""); setInfo("");
  };

  const btn = {
    marginTop: 8, padding: "13px 20px", background: p.ink, color: p.bg, border: "none", borderRadius: 2,
    fontSize: 14, fontWeight: 500, fontFamily: F.display, cursor: "pointer", width: "100%", opacity: busy ? 0.6 : 1,
  };

  return (
    <form data-testid="phone-auth-panel" onSubmit={phone ? verify : send} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {!phone ? (
        <div>
          <Over color={p.ink3}>Mobile number</Over>
          <input data-testid="phone-input" type="tel" inputMode="tel" autoComplete="tel" value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)} placeholder="(416) 555-0123" required style={inputStyle(p)} />
          <p style={{ fontSize: 12, color: p.ink4, margin: "8px 0 0" }}>
            Canada and US numbers work as typed. Add + and a country code for others. Message and data rates may apply.
          </p>
        </div>
      ) : (
        <div>
          <Over color={p.ink3}>Code</Over>
          <input data-testid="phone-otp-input" type="text" inputMode="numeric" autoComplete="one-time-code"
            maxLength={OTP_LENGTH} value={code} onChange={(e) => setCode(cleanOtp(e.target.value))}
            placeholder={"•".repeat(OTP_LENGTH)} required autoFocus
            style={{ ...inputStyle(p), letterSpacing: "0.4em", fontFamily: F.mono }} />
        </div>
      )}

      {error && <p data-testid="phone-auth-error" role="alert" style={{ fontSize: 13, color: "var(--c-bad)", margin: 0 }}>{error}</p>}
      {info && !error && <p style={{ fontSize: 13, color: T.ok, margin: 0 }}>{info}</p>}

      <button data-testid={phone ? "phone-verify-btn" : "phone-send-btn"} type="submit" disabled={busy || (!phone && cooldown > 0)} style={btn}>
        {busy ? "…" : phone ? (mode === "register" ? "Verify and create account" : "Verify and sign in") : "Text me a code"}
      </button>

      <div style={{ fontSize: 12, color: p.ink3, display: "flex", justifyContent: "space-between", gap: 12 }}>
        <button type="button" onClick={phone ? changeNumber : onBack} style={linkBtn}>
          {phone ? "← Use a different number" : "← Use email instead"}
        </button>
        {phone && (
          <button type="button" data-testid="phone-resend-btn" onClick={send} disabled={busy || cooldown > 0}
            style={{ ...linkBtn, color: cooldown > 0 ? p.ink4 : T.brickDeep, cursor: cooldown > 0 ? "default" : "pointer" }}>
            {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
          </button>
        )}
      </div>
    </form>
  );
}
